import { ComponentType, createElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, CircleIcon, CloseIcon, HStack, IconButton, IIconProps, ScrollView, useTheme, View, VStack } from 'native-base';
import { isEmpty, map } from 'lodash';

import { Text, File06Icon } from '@pimm/base';
import { useAppLocale } from '@pimm/common';
import { StringTranslation } from '@pimm/services/lib/sms-tenants';
import { ResourceLoader } from '@app/components/shared';
import { GetReportDataForSiteQueryParams } from '@pimm/services/lib/store-inspections/types';
import { useSimpleSite } from '../context/simple-site.context';
import { useGetSummaryReportsBySite } from '../hooks/get-summary-reports-for-site.hook';
import { useCombineStoreReports, WeekFocus } from '../hooks/combine-store-reports.hook';
import { StoreInspectionPreview } from './store-inspection-preview';
import { StoreReportTable } from './store-report-table';

type StoreInspectionSummaryProps = {
  icon?: ComponentType<IIconProps>;
  title?: string;
  titleTranslations?: StringTranslation[];
  isEditEnabled?: boolean;
  moduleType: number;
  queryReports: ReturnType<typeof useGetSummaryReportsBySite>;
  queryParams?: Partial<GetReportDataForSiteQueryParams>;
  week: WeekFocus;
  onClose?: () => void;
};

export const StoreInspectionSummary = ({ isEditEnabled, queryReports, week, moduleType, ..._props }: StoreInspectionSummaryProps) => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { translate } = useAppLocale();
  const simpleSite = useSimpleSite(_ => _.site);
  const [query, setQuery] = useState<Partial<GetReportDataForSiteQueryParams>>(_props.queryParams ?? {});

  const combinedReports = useCombineStoreReports(simpleSite, week, queryReports.data);

  const tableData = simpleSite?.id ? combinedReports.data[simpleSite.id] : [];
  const isVerified = tableData.every(_ => _.isReviewed);

  return (
    <VStack w="full" h="full">
      {/* Header */}
      <HStack alignItems="center" justifyContent="space-between" py={2} pl={4} pr={3} minH="48px" borderBottomWidth={1}>
        <HStack space={2} flex={1} alignItems="center">
          {createElement(_props.icon ?? File06Icon, { size: '24px', color: colors.gray[700], ..._props.icon })}

          <HStack space={1.5} alignItems="center">
            {!!_props.title && (
              <>
                <Text size="2xl" fontWeight={600} color="gray.800" lineHeight="2xl">
                  {_props.title ? translate(_props.title, _props.titleTranslations) : ''}
                </Text>
                <CircleIcon size={1} color="gray.600" />
              </>
            )}
            <Text size="2xl" fontWeight={600} color="gray.600" lineHeight="2xl">
              {t('store_inspections:report_viewer')}
            </Text>
          </HStack>
        </HStack>

        {!!_props.onClose && (
          <IconButton
            rounded="lg"
            variant="unstyled"
            boxSize={8}
            p={0}
            icon={<CloseIcon size="18px" />}
            _hover={{ bg: 'gray.50' }}
            _pressed={{ bg: 'gray.100' }}
            onPress={_props.onClose}
          />
        )}
      </HStack>

      {/* Body */}
      <HStack flex={1} h="full">
        {/* List View */}
        <View w="1/2" maxWidth={720} borderRightWidth={1}>
          <ResourceLoader flex={1} isEmpty={isEmpty(queryReports.data)} isLoading={!simpleSite?.id || queryReports.isLoading}>
            <ScrollView w="full" h="full" stickyHeaderIndices={[0]} _contentContainerStyle={{ padding: 4, paddingTop: 2 }}>
              {isEditEnabled && !isVerified && (
                <HStack space={2} py={3} bg="white" alignItems="center" justifyContent="end">
                  <Button shadow={0} borderColor="primary.600">
                    {t('store_inspections:verify_all_reports')}
                  </Button>
                </HStack>
              )}
              <VStack space={3} h="full" w="full" mt={2}>
                {map(tableData, _report => (
                  <StoreReportTable
                    key={_report.reportId}
                    isEditEnabled={isEditEnabled}
                    moduleType={moduleType}
                    report={_report}
                    selectedId={query?.storeReportId}
                    size="md"
                    week={week}
                    onSelect={setQuery}
                  />
                ))}
              </VStack>
            </ScrollView>
          </ResourceLoader>
        </View>

        {/* Report Viewer */}
        <View flex={1} h="full" overflow="hidden" roundedBottomRight="xl">
          <StoreInspectionPreview key={query.storeReportId} {...query} siteId={simpleSite?.id} />
        </View>
      </HStack>
    </VStack>
  );
};
